/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, useEffect, memo } from 'react';
import BrandsContext from './BrandsContext';
import initialBrandsState from './initialBrandsState';
import brandsReducer from 'contexts/brands/reducer/brands.reducer';
import * as action from 'contexts/brands/reducer/brands.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import useContextMyBrands from 'hooks/contexts/useContextMyBrands';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';
import { IBrandsBodyFetchState } from 'interfaces/brands.interface';

function BrandsProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(brandsReducer, initialBrandsState);
  const { isLoadingGetMyBrands } = useContextMyBrands();
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { ping } = useContextGeneral();

  useEffect(() => {
    (async () => {
      if (!ping) return false;
      const token = await getAccessTokenSilently();
      if (!isLoadingGetMyBrands && state.allBrands.length <= 1) {
        return await action.getAllBrandsAction({ dispatch, token, translate });
      }
    })();
  }, [isLoadingGetMyBrands, state.allBrands.length, ping]);

  useEffect(() => {
    (async () => {
      if (!ping) return false;
      const token = await getAccessTokenSilently();
      const bodyFetch: IBrandsBodyFetchState = { ...state.bodyFetch, index: 0 };
      return await action.getPaginationBrandsAction({
        dispatch,
        bodyFetch,
        token,
        translate,
      });
    })();
  }, [ping]);

  const addMoreBrands = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return await action.addMoreBrandsAction({ dispatch, state, token, translate });
  }, [state]);

  const setBodySearchGetBrands = useCallback(
    async (query: string | null) => {
      const token = await getAccessTokenSilently();
      return await action.setBodySearchGetBrandsAction({
        dispatch,
        query,
        bodyFetch: state.bodyFetch,
        token,
        translate,
      });
    },
    [state.bodyFetch]
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      addMoreBrands,
      setBodySearchGetBrands,
    }),
    [state, addMoreBrands, setBodySearchGetBrands]
  );

  return (
    <BrandsContext.Provider value={memoProvider}>{props.children}</BrandsContext.Provider>
  );
}

export default memo(BrandsProvider);
